import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import './CarpelLaunchBanner.css';

// Using the trimmed asset where 100% of dead padding has been removed
const carpelLogo = '/images/carpel-dark-trimmed.png';

// Official Launch Target: September 14, 2026 00:00:00 Local Time
// -----------------------------------------------------------------------------
// MANUAL TESTING GUIDE:
// To manually test a 10-second countdown directly in code, change TARGET_DATE to:
//   const TARGET_DATE = Date.now() + 10 * 1000;
// -----------------------------------------------------------------------------
const TARGET_DATE = new Date('2026-09-14T00:00:00').getTime();

// Full-bar confetti pieces spanning 0% to 100% width
const CONFETTI_PIECES = [
  { left: '2%', top: '25%', width: '9px', height: '18px', color: '#ef4444', rot: '20deg', delay: '0s' },
  { left: '6%', top: '65%', width: '11px', height: '11px', color: '#f59e0b', rot: '45deg', delay: '0.8s', shape: 'circle' },
  { left: '10%', top: '30%', width: '8px', height: '16px', color: '#3b82f6', rot: '-30deg', delay: '1.4s' },
  { left: '15%', top: '70%', width: '10px', height: '10px', color: '#10b981', rot: '15deg', delay: '0.3s', shape: 'circle' },
  { left: '19%', top: '20%', width: '9px', height: '18px', color: '#ec4899', rot: '60deg', delay: '2.1s' },
  { left: '24%', top: '55%', width: '8px', height: '16px', color: '#f59e0b', rot: '-40deg', delay: '1.1s' },
  { left: '28%', top: '25%', width: '11px', height: '11px', color: '#06b6d4', rot: '25deg', delay: '0.6s', shape: 'circle' },
  { left: '33%', top: '78%', width: '9px', height: '17px', color: '#8b5cf6', rot: '-15deg', delay: '1.9s' },
  { left: '38%', top: '12%', width: '10px', height: '15px', color: '#ef4444', rot: '35deg', delay: '2.5s' },
  { left: '43%', top: '82%', width: '8px', height: '16px', color: '#10b981', rot: '-50deg', delay: '0.4s' },
  { left: '48%', top: '14%', width: '12px', height: '12px', color: '#f59e0b', rot: '10deg', delay: '1.7s', shape: 'circle' },
  { left: '53%', top: '80%', width: '9px', height: '19px', color: '#3b82f6', rot: '-25deg', delay: '0.9s' },
  { left: '58%', top: '12%', width: '8px', height: '16px', color: '#ec4899', rot: '45deg', delay: '2.3s' },
  { left: '63%', top: '76%', width: '11px', height: '11px', color: '#10b981', rot: '-35deg', delay: '1.3s', shape: 'circle' },
  { left: '68%', top: '28%', width: '9px', height: '17px', color: '#f59e0b', rot: '20deg', delay: '0.5s' },
  { left: '73%', top: '75%', width: '8px', height: '16px', color: '#ef4444', rot: '-45deg', delay: '1.8s' },
  { left: '78%', top: '18%', width: '10px', height: '10px', color: '#06b6d4', rot: '30deg', delay: '2.7s', shape: 'circle' },
  { left: '83%', top: '60%', width: '9px', height: '18px', color: '#8b5cf6', rot: '-20deg', delay: '0.7s' },
  { left: '88%', top: '25%', width: '8px', height: '15px', color: '#f59e0b', rot: '55deg', delay: '1.5s' },
  { left: '92%', top: '70%', width: '11px', height: '11px', color: '#3b82f6', rot: '-10deg', delay: '2.0s', shape: 'circle' },
  { left: '96%', top: '30%', width: '9px', height: '17px', color: '#ef4444', rot: '40deg', delay: '0.2s' },
  { left: '99%', top: '62%', width: '8px', height: '15px', color: '#10b981', rot: '-30deg', delay: '1.2s' },
];

const CONFETTI_SPARKLES = [
  { left: '4%', top: '45%', color: '#f59e0b', icon: '✦', delay: '0s' },
  { left: '17%', top: '22%', color: '#ef4444', icon: '✷', delay: '0.7s' },
  { left: '30%', top: '75%', color: '#3b82f6', icon: '✦', delay: '1.3s' },
  { left: '46%', top: '14%', color: '#10b981', icon: '⬖', delay: '1.9s' },
  { left: '60%', top: '80%', color: '#ec4899', icon: '✦', delay: '0.4s' },
  { left: '75%', top: '28%', color: '#f59e0b', icon: '✷', delay: '1.6s' },
  { left: '86%', top: '65%', color: '#8b5cf6', icon: '✦', delay: '2.2s' },
  { left: '95%', top: '22%', color: '#ef4444', icon: '✦', delay: '1.0s' },
];

// Continuous full-bar Marigold Petals (Genda Phool) spanning 0% to 100% width
const GANESH_PETALS = [
  { left: '1.5%', size: 13, rot: 25, delay: '-0.3s' },
  { left: '5%', size: 10, rot: -30, delay: '-2.4s' },
  { left: '8.5%', size: 14, rot: 45, delay: '-1.1s' },
  { left: '12%', size: 11, rot: -15, delay: '-3.7s' },
  { left: '16%', size: 12, rot: 20, delay: '-0.8s' },
  { left: '20%', size: 15, rot: -40, delay: '-2.9s' },
  { left: '23.5%', size: 10, rot: 35, delay: '-1.6s' },
  { left: '27%', size: 13, rot: -20, delay: '-4.2s' },
  { left: '31%', size: 12, rot: 15, delay: '-0.5s' },
  { left: '35%', size: 14, rot: -35, delay: '-2.2s' },
  { left: '39%', size: 11, rot: 30, delay: '-3.4s' },
  { left: '43%', size: 13, rot: -25, delay: '-1.3s' },
  { left: '47%', size: 15, rot: 40, delay: '-4.0s' },
  { left: '51%', size: 10, rot: -10, delay: '-0.2s' },
  { left: '55%', size: 14, rot: 25, delay: '-2.7s' },
  { left: '59%', size: 11, rot: -45, delay: '-1.8s' },
  { left: '63%', size: 13, rot: 30, delay: '-3.6s' },
  { left: '67%', size: 12, rot: -20, delay: '-0.9s' },
  { left: '71%', size: 15, rot: 35, delay: '-2.5s' },
  { left: '75%', size: 10, rot: -30, delay: '-4.4s' },
  { left: '79%', size: 13, rot: 15, delay: '-1.5s' },
  { left: '83%', size: 14, rot: -40, delay: '-3.1s' },
  { left: '87%', size: 11, rot: 25, delay: '-0.6s' },
  { left: '90.5%', size: 13, rot: -15, delay: '-2.8s' },
  { left: '94%', size: 15, rot: 35, delay: '-1.9s' },
  { left: '97.5%', size: 11, rot: -25, delay: '-3.9s' },
];

// Marigold petals specifically balanced for the Welcome Modal width
const MODAL_GANESH_PETALS = [
  { left: '6%', size: 12, delay: '-0.3s' },
  { left: '14%', size: 10, delay: '-1.8s' },
  { left: '22%', size: 14, delay: '-0.9s' },
  { left: '30%', size: 11, delay: '-2.4s' },
  { left: '38%', size: 13, delay: '-0.1s' },
  { left: '46%', size: 10, delay: '-1.5s' },
  { left: '54%', size: 15, delay: '-2.7s' },
  { left: '62%', size: 12, delay: '-0.6s' },
  { left: '70%', size: 14, delay: '-2.1s' },
  { left: '78%', size: 11, delay: '-1.2s' },
  { left: '86%', size: 13, delay: '-2.9s' },
  { left: '94%', size: 10, delay: '-0.4s' },
];

const GANESH_SPARKLES = [
  { left: '3%', top: '45%', delay: '0.2s' },
  { left: '12%', top: '22%', delay: '1.4s' },
  { left: '22%', top: '70%', delay: '0.8s' },
  { left: '33%', top: '28%', delay: '1.8s' },
  { left: '44%', top: '68%', delay: '0.5s' },
  { left: '55%', top: '20%', delay: '1.1s' },
  { left: '65%', top: '74%', delay: '1.6s' },
  { left: '76%', top: '24%', delay: '0.3s' },
  { left: '86%', top: '65%', delay: '2.0s' },
  { left: '95%', top: '35%', delay: '1.3s' },
];

// Auspicious Ganesha Vector Artwork - High-Visibility Sacred Design
function GaneshMotifSvg({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ganeshGoldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#ea580c" />
        </linearGradient>
        <radialGradient id="ganeshAura" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fed7aa" stopOpacity="0.85" />
          <stop offset="70%" stopColor="#fdba74" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#ea580c" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Divine Golden Aura Halo */}
      <circle cx="50" cy="46" r="39" fill="url(#ganeshAura)" />
      <circle cx="50" cy="46" r="37" stroke="#f59e0b" strokeWidth="1" strokeDasharray="3 3" opacity="0.65" />

      {/* Royal Crown / Mukut */}
      <path
        d="M36 24 L50 6 L64 24 Z"
        fill="url(#ganeshGoldGrad)"
        stroke="#c2410c"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <path d="M42 16 L50 8 L58 16" stroke="#ffffff" strokeWidth="1.4" strokeLinecap="round" opacity="0.95" />
      <circle cx="50" cy="14" r="2.5" fill="#ffffff" />
      <rect x="34" y="23" width="32" height="4.5" rx="2" fill="#c2410c" stroke="#9a3412" strokeWidth="0.8" />
      <circle cx="42" cy="25.2" r="1.1" fill="#fbbf24" />
      <circle cx="50" cy="25.2" r="1.3" fill="#ffffff" />
      <circle cx="58" cy="25.2" r="1.1" fill="#fbbf24" />

      {/* Forehead Brow Line */}
      <path d="M38 28.5 C42 28.5 46 30 50 30 C54 30 58 28.5 62 28.5" stroke="#ea580c" strokeWidth="2.4" strokeLinecap="round" />

      {/* Sacred Tilak (Red Kumkum Trident + Chandan) */}
      <path d="M50 28 L50 39" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
      <path d="M44 32 Q50 38 56 32" stroke="#f59e0b" strokeWidth="2.4" fill="none" strokeLinecap="round" />
      <circle cx="50" cy="41" r="2" fill="#dc2626" />

      {/* Left Ear (Supakarna) with cream fill & gold inner fold */}
      <path
        d="M36 28 C20 28 10 38 13 54 C15 63 26 66 33 62"
        stroke="#ea580c"
        strokeWidth="2.8"
        fill="#fffbf5"
        strokeLinecap="round"
      />
      <path d="M22 42 C24 50 28 54 32 55" stroke="#f59e0b" strokeWidth="1.8" fill="none" strokeLinecap="round" />

      {/* Right Ear */}
      <path
        d="M64 28 C80 28 90 38 87 54 C85 63 74 66 67 62"
        stroke="#ea580c"
        strokeWidth="2.8"
        fill="#fffbf5"
        strokeLinecap="round"
      />
      <path d="M78 42 C76 50 72 54 68 55" stroke="#f59e0b" strokeWidth="1.8" fill="none" strokeLinecap="round" />

      {/* Vakratunda Trunk with auspicious sacred curve */}
      <path
        d="M44 41 C44 53 46 65 52 73 C58 81 72 79 72 69 C72 61 63 61 60 66"
        stroke="#c2410c"
        strokeWidth="3.4"
        fill="none"
        strokeLinecap="round"
      />

      {/* Sacred Ekadanta Tusk */}
      <path d="M42 55 L34 57" stroke="#ffffff" strokeWidth="3.2" strokeLinecap="round" />
      <path d="M42 55 L34 57" stroke="#ea580c" strokeWidth="1.2" fill="none" strokeLinecap="round" />

      {/* Golden Modak / Laddu */}
      <circle cx="72" cy="67" r="5" fill="#f59e0b" stroke="#c2410c" strokeWidth="1.5" />
      <circle cx="70.5" cy="65.5" r="1.5" fill="#fef08a" />
    </svg>
  );
}

// Marigold Toran Garland with repeating Blossoms & Mango leaves
function MarigoldToranGarland() {
  return (
    <div className="ganesh-toran-container" aria-hidden="true">
      <svg className="ganesh-toran-svg" width="100%" height="16" preserveAspectRatio="none">
        <defs>
          <pattern id="marigold-toran-pat" width="44" height="16" patternUnits="userSpaceOnUse">
            {/* Hanging thread arc */}
            <path d="M 0 3 Q 22 11 44 3" stroke="#d97706" strokeWidth="1.2" fill="none" opacity="0.8" />
            {/* Sacred Mango leaf in center */}
            <path d="M 22 7 C 19 12 19 14 22 16 C 25 14 25 12 22 7 Z" fill="#15803d" />
            {/* Yellow Marigold */}
            <circle cx="9" cy="5" r="4.5" fill="#f59e0b" />
            <circle cx="9" cy="5" r="2.5" fill="#fbbf24" />
            {/* Center Orange Marigold */}
            <circle cx="22" cy="6" r="5" fill="#ea580c" />
            <circle cx="22" cy="6" r="3" fill="#f97316" />
            <circle cx="22" cy="6" r="1.5" fill="#fef08a" />
            {/* Yellow Marigold */}
            <circle cx="35" cy="5" r="4.5" fill="#f59e0b" />
            <circle cx="35" cy="5" r="2.5" fill="#fbbf24" />
          </pattern>
        </defs>
        <rect width="100%" height="16" fill="url(#marigold-toran-pat)" />
      </svg>
    </div>
  );
}

export default function CarpelLaunchBanner() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Active theme: Locked to Ganesh Chaturthi theme
  const currentTheme = searchParams.get('theme') || 'ganesh';

  // Support test simulation of launch live state: ?live=true
  const isSimulatedLive = searchParams.get('live') === 'true';

  // Check if test mode is requested via URL query (?test=true, ?test=10s, or ?test=1m)
  const isTenSecTest = searchParams.get('test') === '10s';
  const isOneMinTest = searchParams.get('test') === '1m';
  const isTimerTest = isTenSecTest || isOneMinTest;
  const showTestToolbar = searchParams.get('test') === 'true' || isTimerTest || searchParams.get('debug') === 'true';

  // Modal visibility: active on first visit
  const [showModal, setShowModal] = useState(false);
  // Jumbo topbar remains visible at top
  const [showTopbar, setShowTopbar] = useState(true);

  // Short Testing Target (10s or 60s): initialized on demand when ?test=10s or ?test=1m
  const [shortTestTarget, setShortTestTarget] = useState(() => {
    if (!isTimerTest) return null;
    const duration = isTenSecTest ? 10 * 1000 : 60 * 1000;
    try {
      const saved = sessionStorage.getItem('carpel_test_target_time');
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (parsed > Date.now()) return parsed;
      }
      const initial = Date.now() + duration;
      sessionStorage.setItem('carpel_test_target_time', String(initial));
      return initial;
    } catch {
      return Date.now() + duration;
    }
  });

  // Calculate time remaining:
  // - If simulated live (?live=true): 00:00:00:00 isLive: true
  // - If short test (?test=10s or ?test=1m): counts down 10s or 60s
  // - Default production mode: counts down to official TARGET_DATE (Sept 14, 2026)
  const calculateTimeRemaining = useCallback(() => {
    if (isSimulatedLive) {
      return { isLive: true, days: '00', hours: '00', minutes: '00', seconds: '00' };
    }
    const target = isTimerTest && shortTestTarget ? shortTestTarget : TARGET_DATE;
    const diff = target - Date.now();
    if (diff <= 0) {
      return { isLive: true, days: '00', hours: '00', minutes: '00', seconds: '00' };
    }
    const d = Math.floor(diff / (1000 * 60 * 60 * 24));
    const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const m = Math.floor((diff / 1000 / 60) % 60);
    const s = Math.floor((diff / 1000) % 60);
    return {
      isLive: false,
      days: String(d).padStart(2, '0'),
      hours: String(h).padStart(2, '0'),
      minutes: String(m).padStart(2, '0'),
      seconds: String(s).padStart(2, '0'),
    };
  }, [isSimulatedLive, isTimerTest, shortTestTarget]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeRemaining);

  // Check visit state on mount
  useEffect(() => {
    try {
      const modalDismissedInSession = sessionStorage.getItem('carpel_banner_dismissed_session');
      if (!modalDismissedInSession) {
        setShowModal(true);
      }
    } catch {
      setShowModal(true);
    }

    try {
      const topbarClosed = sessionStorage.getItem('carpel_topbar_closed');
      if (topbarClosed === 'true') {
        setShowTopbar(false);
      }
    } catch {
      // Ignore
    }
  }, []);

  // Real-time clock tick every 1000ms
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeRemaining());
    }, 1000);
    return () => clearInterval(interval);
  }, [calculateTimeRemaining]);

  // Dismiss modal
  const handleCloseModal = () => {
    setShowModal(false);
    try {
      sessionStorage.setItem('carpel_banner_dismissed_session', 'true');
    } catch {
      // Ignore
    }
  };

  // Dismiss topbar
  const handleCloseTopbar = () => {
    setShowTopbar(false);
    try {
      sessionStorage.setItem('carpel_topbar_closed', 'true');
    } catch {
      // Ignore
    }
  };

  // Keyboard accessibility
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showModal) {
        handleCloseModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal]);

  // Test Toolbar Handlers
  const startTenSecTest = () => {
    const newTarget = Date.now() + 10 * 1000;
    try {
      sessionStorage.setItem('carpel_test_target_time', String(newTarget));
    } catch {
      // Ignore
    }
    setShortTestTarget(newTarget);
    searchParams.set('test', '10s');
    searchParams.delete('live');
    setSearchParams(searchParams, { replace: true });
  };

  const startOneMinTest = () => {
    const newTarget = Date.now() + 60 * 1000;
    try {
      sessionStorage.setItem('carpel_test_target_time', String(newTarget));
    } catch {
      // Ignore
    }
    setShortTestTarget(newTarget);
    searchParams.set('test', '1m');
    searchParams.delete('live');
    setSearchParams(searchParams, { replace: true });
  };

  const switchToOfficialLaunch = () => {
    searchParams.set('test', 'true');
    searchParams.delete('live');
    setSearchParams(searchParams, { replace: true });
  };

  // Toggle live simulation state
  const toggleLiveSimulation = () => {
    if (isSimulatedLive) {
      searchParams.delete('live');
    } else {
      searchParams.set('live', 'true');
    }
    setSearchParams(searchParams, { replace: true });
  };

  const closeTestToolbar = () => {
    searchParams.delete('test');
    searchParams.delete('live');
    searchParams.delete('theme');
    searchParams.delete('effect');
    searchParams.delete('debug');
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <>
      {/* 1. JUMBO STICKY TOPBAR ANNOUNCEMENT */}
      {showTopbar && (
        <div
          className={`carpel-jumbo-topbar ${currentTheme === 'ganesh' ? 'carpel-ganesh-topbar' : ''}`}
          role="banner"
          aria-label="Carpel Product Launch Announcement"
        >

          {/* THEME 1: GANESH CHATURTHI AUSPICIOUS LAUNCH PATTERN */}
          {currentTheme === 'ganesh' && (
            <div className={`carpel-ganesh-bg ${timeLeft.isLive ? 'ganesh-bg-live' : ''}`} aria-hidden="true">
              {/* Marigold Toran Garland across top edge */}
              <MarigoldToranGarland />

              {/* Background Sacred Divine Ganesha Motif Watermark with Golden Radiance */}
              <div className="ganesh-bg-watermark-wrap">
                <GaneshMotifSvg className={`ganesh-bg-watermark ${timeLeft.isLive ? 'ganesh-watermark-live' : ''}`} />
              </div>

              {/* Auspicious Ganesha Motifs on Left & Right in background */}
              <GaneshMotifSvg className={`ganesh-motif-svg ganesh-left ${timeLeft.isLive ? 'ganesh-live-motif' : ''}`} />
              <GaneshMotifSvg className={`ganesh-motif-svg ganesh-right ${timeLeft.isLive ? 'ganesh-live-motif' : ''}`} />

              {/* Continuous Flowing Marigold Petal Shower across full background */}
              {GANESH_PETALS.map((petal, i) => (
                <span
                  key={i}
                  className={`ganesh-petal ${timeLeft.isLive ? 'ganesh-petal-live' : 'ganesh-petal-countdown'}`}
                  style={{
                    left: petal.left,
                    width: `${petal.size}px`,
                    height: `${Math.round(petal.size * 1.35)}px`,
                    animationDelay: petal.delay,
                  }}
                />
              ))}

              {/* Sacred Golden Diya Sparkles */}
              {GANESH_SPARKLES.map((sp, i) => (
                <span
                  key={i}
                  className={`f-sparkle ${timeLeft.isLive ? 'f-sparkle-live' : ''}`}
                  style={{
                    left: sp.left,
                    top: sp.top,
                    color: '#f59e0b',
                    animationDelay: sp.delay,
                  }}
                >
                  ✦
                </span>
              ))}
            </div>
          )}

          {/* THEME 2: FULL-BAR CELEBRATION CONFETTI & LAUNCH ALERT */}
          {currentTheme === 'confetti' && (
            <div className="carpel-full-confetti-bg" aria-hidden="true">
              {/* Floating Confetti across 0% - 100% width (Celebration cascade on live launch) */}
              {CONFETTI_PIECES.map((cp, i) => (
                <span
                  key={i}
                  className={`f-confetti ${timeLeft.isLive ? 'f-confetti-live' : ''}`}
                  style={{
                    left: cp.left,
                    top: cp.top,
                    width: cp.width,
                    height: cp.height,
                    backgroundColor: cp.color,
                    borderRadius: cp.shape === 'circle' ? '50%' : '2px',
                    transform: `rotate(${cp.rot})`,
                    animationDelay: cp.delay,
                  }}
                />
              ))}

              {/* Alert & Celebration Twinkling Sparkles */}
              {CONFETTI_SPARKLES.map((sp, i) => (
                <span
                  key={i}
                  className={`f-sparkle ${timeLeft.isLive ? 'f-sparkle-live' : ''}`}
                  style={{
                    left: sp.left,
                    top: sp.top,
                    color: sp.color,
                    animationDelay: sp.delay,
                  }}
                >
                  {sp.icon}
                </span>
              ))}
            </div>
          )}

          {/* Topbar Content (Z-indexed above background effects for 100% visibility) */}
          <div className="carpel-jumbo-content">
            <img
              src={carpelLogo}
              alt="Carpel"
              className="carpel-jumbo-logo"
              onClick={() => setShowModal(true)}
              style={{ cursor: 'pointer' }}
              title="Click to view launch details"
            />

            {/* Badges */}
            {currentTheme === 'ganesh' && (
              <span
                className={`ganesh-festive-badge ${timeLeft.isLive ? 'ganesh-live-badge' : ''}`}
                title={timeLeft.isLive ? 'Carpel is officially live!' : 'Ganesh Chaturthi Launch'}
              >
                <GaneshMotifSvg className="ganesh-badge-icon" />
                {timeLeft.isLive ? 'SHUBH AARAMBH • NOW LIVE' : 'SHREE GANESHAY NAMAH'}
              </span>
            )}

            {currentTheme === 'confetti' && (
              <span
                className={`confetti-alert-badge ${timeLeft.isLive ? 'confetti-live-badge' : ''}`}
                title={timeLeft.isLive ? 'Carpel 1.0 Live Worldwide' : 'Official Launch Alert'}
              >
                <span className={timeLeft.isLive ? 'alert-pulse-live' : 'alert-pulse-dot'}></span>
                {timeLeft.isLive ? '1.0 RELEASE IS LIVE' : 'LAUNCH ALERT'}
              </span>
            )}

            <span
              className={`carpel-jumbo-title ${currentTheme === 'ganesh' ? 'carpel-ganesh-title' : ''}`}
              onClick={() => setShowModal(true)}
              style={{ cursor: 'pointer' }}
              title="Click to view launch details"
            >
              {timeLeft.isLive ? 'CARPEL IS OFFICIALLY LIVE:' : 'LAUNCH COUNTDOWN:'}
            </span>

            {/* Countdown / Celebratory Live Pill */}
            {timeLeft.isLive ? (
              <span
                className={`carpel-jumbo-pill ${
                  currentTheme === 'ganesh'
                    ? 'carpel-ganesh-live-pill'
                    : 'carpel-confetti-live-pill'
                }`}
              >
                {currentTheme === 'ganesh' ? '🪔 NOW LIVE • LAUNCHED' : '🎉 NOW LIVE WORLDWIDE'}
              </span>
            ) : (
              <span className={`carpel-jumbo-pill ${currentTheme === 'ganesh' ? 'carpel-ganesh-pill' : ''}`}>
                {`${timeLeft.days}D : ${timeLeft.hours}H : ${timeLeft.minutes}M : ${timeLeft.seconds}S`}
              </span>
            )}

            {/* Action Button */}
            <a
              href="https://carpel.io"
              target="_blank"
              rel="noopener noreferrer"
              className={`carpel-jumbo-btn ${
                currentTheme === 'ganesh'
                  ? (timeLeft.isLive ? 'carpel-ganesh-live-btn' : 'carpel-ganesh-btn')
                  : (timeLeft.isLive ? 'carpel-confetti-live-btn' : '')
              }`}
            >
              {timeLeft.isLive
                ? (currentTheme === 'ganesh' ? 'EXPERIENCE CARPEL NOW →' : 'START AUTOMATING →')
                : 'EXPLORE CARPEL →'}
            </a>
          </div>

          <button
            className="carpel-jumbo-close"
            onClick={handleCloseTopbar}
            title="Dismiss announcement bar"
            aria-label="Dismiss announcement bar"
          >
            &times;
          </button>
        </div>
      )}

      {/* 2. WELCOME BANNER MODAL */}
      {showModal && (
        <div
          className="carpel-modal-backdrop"
          onClick={handleCloseModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="carpel-modal-heading"
        >
          <div
            className={`carpel-modal-card ${currentTheme === 'ganesh' ? 'modal-ganesh' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Background Festive Effect for Theme 1 in Modal (both countdown & live) */}
            {currentTheme === 'ganesh' && (
              <div className={`modal-ganesh-bg ${timeLeft.isLive ? 'modal-ganesh-bg-live' : ''}`} aria-hidden="true">
                <MarigoldToranGarland />
                {MODAL_GANESH_PETALS.map((petal, i) => (
                  <span
                    key={i}
                    className={`ganesh-petal ${timeLeft.isLive ? 'modal-petal-live' : 'modal-petal-countdown'}`}
                    style={{
                      left: petal.left,
                      width: `${petal.size}px`,
                      height: `${Math.round(petal.size * 1.35)}px`,
                      animationDelay: petal.delay,
                    }}
                  />
                ))}
                {GANESH_SPARKLES.slice(0, 6).map((sp, i) => (
                  <span
                    key={i}
                    className={`f-sparkle ${timeLeft.isLive ? 'f-sparkle-live' : ''}`}
                    style={{
                      left: sp.left,
                      top: sp.top,
                      color: '#f59e0b',
                      animationDelay: sp.delay,
                    }}
                  >
                    ✦
                  </span>
                ))}
              </div>
            )}
            <button
              className="carpel-modal-close"
              onClick={handleCloseModal}
              aria-label="Close welcome banner"
              title="Close banner"
            >
              &times;
            </button>

            <div className="carpel-modal-header">
              {currentTheme === 'ganesh' && (
                <div className="ganesh-modal-emblem-wrap">
                  <GaneshMotifSvg className={`ganesh-modal-emblem ${timeLeft.isLive ? 'ganesh-live-motif' : ''}`} />
                </div>
              )}
              <div>
                <img src={carpelLogo} alt="Carpel Platform" className="carpel-modal-logo" />
              </div>
              <div>
                <span className={`carpel-modal-badge ${timeLeft.isLive && currentTheme === 'ganesh' ? 'ganesh-live-badge' : ''}`}>
                  {currentTheme === 'ganesh'
                    ? (timeLeft.isLive ? '🪔 SHUBH AARAMBH • NOW LIVE' : 'SHREE GANESHAY NAMAH • CARPEL LAUNCH')
                    : (timeLeft.isLive ? '🎉 OFFICIAL PRODUCTION RELEASE' : 'Official Product Launch')}
                </span>
              </div>
              <h2 id="carpel-modal-heading" className="carpel-modal-title">
                {timeLeft.isLive
                  ? (currentTheme === 'ganesh' ? 'CARPEL IS OFFICIALLY LIVE!' : 'CARPEL 1.0 IS NOW LIVE!')
                  : 'LAUNCHING SEPTEMBER 14'}
              </h2>
              <p className="carpel-modal-sub">
                {timeLeft.isLive
                  ? (currentTheme === 'ganesh'
                      ? 'Celebrating Ganesh Chaturthi with the official release of Carpel. Autonomous AI agents that navigate, test, and execute browser workflows are now live and ready.'
                      : 'Autonomous AI agents that navigate, test, and execute real browser workflows automatically are ready to accelerate your QA and web operations.')
                  : 'Autonomous AI agents that navigate, test, and execute real browser workflows automatically.'}
              </p>
            </div>

            {/* Countdown Grid (pre-launch) OR Celebratory Card (time ended / live) */}
            {timeLeft.isLive ? (
              <div className={`modal-live-celebration-card ${currentTheme === 'ganesh' ? 'modal-live-ganesh' : 'modal-live-confetti'}`}>
                <div className="live-card-icon">{currentTheme === 'ganesh' ? '🪔' : '🚀'}</div>
                <div className="live-card-text">
                  <strong>
                    {currentTheme === 'ganesh'
                      ? 'CARPEL IS OFFICIALLY LIVE'
                      : 'ALL SYSTEMS OPERATIONAL • LIVE RELEASE'}
                  </strong>
                  <span>
                    {currentTheme === 'ganesh'
                      ? 'All AI browser automation agents are active and ready to test your applications.'
                      : 'Autonomous agents ready to execute QA regression suites and enterprise workflows.'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="carpel-countdown-grid" aria-label="Time remaining until launch">
                <div className="carpel-timer-tile">
                  <div className="carpel-timer-num">{timeLeft.days}</div>
                  <div className="carpel-timer-unit">Days</div>
                </div>
                <span className="carpel-timer-sep" aria-hidden="true">:</span>
                <div className="carpel-timer-tile">
                  <div className="carpel-timer-num">{timeLeft.hours}</div>
                  <div className="carpel-timer-unit">Hrs</div>
                </div>
                <span className="carpel-timer-sep" aria-hidden="true">:</span>
                <div className="carpel-timer-tile">
                  <div className="carpel-timer-num">{timeLeft.minutes}</div>
                  <div className="carpel-timer-unit">Min</div>
                </div>
                <span className="carpel-timer-sep" aria-hidden="true">:</span>
                <div className="carpel-timer-tile">
                  <div className="carpel-timer-num">{timeLeft.seconds}</div>
                  <div className="carpel-timer-unit">Sec</div>
                </div>
              </div>
            )}

            {/* Call To Action */}
            <div>
              <a
                href="https://carpel.io"
                target="_blank"
                rel="noopener noreferrer"
                className="carpel-btn-primary"
                onClick={handleCloseModal}
              >
                {timeLeft.isLive
                  ? (currentTheme === 'ganesh' ? 'ENTER CARPEL PLATFORM →' : 'START AUTOMATING NOW →')
                  : 'EXPLORE CARPEL.IO'}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* 3. TEST CONTROLS TOOLBAR (Visible strictly when ?test=true, ?test=10s, or ?test=1m is in URL) */}
      {showTestToolbar && (
        <div className="carpel-theme-switcher" role="toolbar" aria-label="Testing Controls">
          <span className="carpel-theme-tag">TEST CONTROLS</span>
          <span className="carpel-theme-label">
            {isTenSecTest
              ? '⚡ 10-Sec Test Mode'
              : (isOneMinTest
                  ? '⚡ 1-Min Test Mode'
                  : (timeLeft.isLive ? '🎉 Live Launch Mode' : '📅 Official Sept 14 Launch'))}
          </span>

          <button
            className="carpel-theme-live-toggle"
            onClick={startTenSecTest}
            title="Run a 10-second test countdown"
          >
            ⚡ 10-Sec Test
          </button>

          <button
            className="carpel-theme-live-toggle"
            onClick={startOneMinTest}
            title="Run a 60-second test countdown"
          >
            ⚡ 1-Min Test
          </button>

          {isTimerTest && (
            <button
              className="carpel-theme-live-toggle"
              onClick={switchToOfficialLaunch}
              title="Switch to official Sept 14 launch countdown"
            >
              📅 Official Date
            </button>
          )}

          <button
            className={`carpel-theme-live-toggle ${timeLeft.isLive ? 'active-live' : ''}`}
            onClick={toggleLiveSimulation}
            title={timeLeft.isLive ? 'Switch back to Countdown view' : 'Simulate Live celebration view'}
          >
            {timeLeft.isLive ? '⏱ Countdown Mode' : '🎉 Live Mode'}
          </button>

          <button
            className="carpel-theme-modal-btn"
            onClick={() => setShowModal(true)}
            title="Preview Welcome Modal"
          >
            Preview Modal
          </button>

          <button
            className="carpel-theme-close-btn"
            onClick={closeTestToolbar}
            title="Hide test toolbar"
            aria-label="Hide test toolbar"
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
}
